/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { TimeFieldState, TimeSegmentState } from './getTimeFieldInitialState';
import { OrderedTimeSegmentsArray, EditableTimeSegmentType } from './types';
import {
  displayedTimeSegmentsAreDifferent,
  hasSegmentsNotDisplayed
} from '#utils/UNSAFE_timeUtils';
import { numberToString, stringToNumber, withinRange } from '#utils/PRIVATE_dayUtils/maskUtils';

type DirectionType = 'increase' | 'decrease';

type SegmentActionData = Pick<TimeSegmentState, 'type'>;

type DayPeriodSegmentActionData = SegmentActionData & {
  'aria-valuenow'?: number;
};

type PageStepActionData = SegmentActionData & {
  direction: DirectionType;
};

type UpdateActionData = SegmentActionData & {
  text: string;
};

// Describes all possible actions for interacting with the time segments.
// For example,
// selectSegment: focuses on one segment (like hour or minute).
// updateSegment: called when the user types in a number. Figures out if the segment is complete.
// step: Increments or decrements the value in the segment.
// commit: Finalizes changes made to the segments.
type TimeReducerAction =
  | { actionType: 'clearTime'; data?: never }
  | { actionType: 'clearSegment'; data: SegmentActionData }
  | { actionType: 'commitOnBlur'; data?: never }
  | { actionType: 'commitOnEnter'; data?: never }
  | { actionType: 'committed'; data?: never }
  | { actionType: 'goToEnd'; data: SegmentActionData }
  | { actionType: 'goToHome'; data: SegmentActionData }
  | { actionType: 'page'; data: PageStepActionData }
  | { actionType: 'selectFirst'; data?: never }
  | { actionType: 'selectNext'; data?: never }
  | { actionType: 'selectPrevious'; data?: never }
  | { actionType: 'selectSegment'; data: SegmentActionData }
  | { actionType: 'selectTime'; data?: never }
  | { actionType: 'step'; data: PageStepActionData }
  | { actionType: 'updateDayPeriodSegment'; data: DayPeriodSegmentActionData }
  | { actionType: 'updateSegment'; data: UpdateActionData }
  | { actionType: 'reset'; data: TimeFieldState };

const AM_VALUE = 0;
const PM_VALUE = 12;

/**
 * Reducer function for updating TimeFieldState per the specified TimeReducerAction.
 * @param state
 * @param action
 */
const timeReducer = (state: TimeFieldState, action: TimeReducerAction) => {
  // On mount, the reducer is initialized with initial state derived from the TimeField's value.
  // If that value is changed programmatically afterwards, the only way to push that update
  // into the reducer is to define a "reset" action.
  if (action.actionType === 'reset') {
    return action.data;
  }
  // Each action updates the state using helper functions to modify a specific segment or
  // change focus between segments.
  switch (action.actionType) {
    case 'clearTime': {
      // Resets all segments to the default (useful when clearing the entire time)
      const updatedSegments = selectFirst(clearTime(state.segments), state.orderedSegments);
      return createTimeFieldStateFromUpdatedSegments(state, updatedSegments, false);
    }
    case 'clearSegment': {
      const updatedSegment = clearSegment(findSegment(state.segments, action.data.type));
      return createTimeFieldStateFromUpdatedSegment(state, action.data.type, updatedSegment);
    }
    case 'commitOnEnter': {
      const updatedSegments = completeAllSegmentsBasedOnHour(state.segments);
      const newState = createTimeFieldStateFromUpdatedSegments(state, updatedSegments, false);
      return { ...newState, isCommitting: true };
    }
    case 'commitOnBlur': {
      let updatedSegments = completeAllSegmentsBasedOnHour(state.segments);
      updatedSegments = deselectAndComplete(updatedSegments);
      const newState = createTimeFieldStateFromUpdatedSegments(state, updatedSegments, false);
      return { ...newState, isCommitting: true };
    }
    case 'committed': {
      return { ...state, isCommitting: false };
    }
    case 'goToEnd': {
      const updatedSegment = goToEnd(
        action.data.type,
        findSegment(state.segments, action.data.type)
      );
      return createTimeFieldStateFromUpdatedSegment(state, action.data.type, updatedSegment, false);
    }
    case 'goToHome': {
      const updatedSegment = goToHome(
        action.data.type,
        findSegment(state.segments, action.data.type)
      );
      return createTimeFieldStateFromUpdatedSegment(state, action.data.type, updatedSegment, false);
    }
    case 'page': {
      const direction = action.data.direction;
      if (direction === undefined) return state;
      const updatedSegment = doStepOrPage(
        action.data.type,
        findSegment(state.segments, action.data.type),
        'page',
        direction
      );
      return createTimeFieldStateFromUpdatedSegment(state, action.data.type, updatedSegment, false);
    }
    case 'selectTime': {
      return selectTime(state);
    }
    case 'selectFirst': {
      const updatedSegments = selectFirst(state.segments, state.orderedSegments);
      return createTimeFieldStateFromUpdatedSegments(state, updatedSegments, false);
    }
    // Select the segment following the current one.
    case 'selectNext': {
      // If hour has a value and is currently selected when we get the selectNext action, then auto-fill
      // the empty segments.
      const updatedSegments = completeAllEmptySegmentsWhenHourHasValue(state.segments, true);
      const updatedSelectedNextSegments = selectNext(updatedSegments, state.orderedSegments);
      return createTimeFieldStateFromUpdatedSegments(state, updatedSelectedNextSegments, false);
    }
    case 'selectPrevious': {
      const updatedSegments = selectPrevious(state.segments, state.orderedSegments);
      return createTimeFieldStateFromUpdatedSegments(state, updatedSegments, false);
    }
    case 'selectSegment': {
      const updatedSegments = selectSegment(state.segments, action.data.type);
      return createTimeFieldStateFromUpdatedSegments(state, updatedSegments, false);
    }
    // Increments or decrements the current segment value.
    case 'step': {
      const direction = action.data.direction;
      if (direction === undefined) return state;
      const updatedSegment = doStepOrPage(
        action.data.type,
        findSegment(state.segments, action.data.type),
        'step',
        direction
      );
      return createTimeFieldStateFromUpdatedSegment(state, action.data.type, updatedSegment, false);
    }
    case 'updateDayPeriodSegment': {
      if (action.data.type === 'dayPeriod') {
        const newValue = action.data['aria-valuenow'];
        if (newValue === PM_VALUE || newValue === AM_VALUE) {
          // we have a new value for the dayPeriod. Create a new state.
          const updatedSegment = createSegmentFromKeyboardAction(
            action.data.type,
            newValue,
            findSegment(state.segments, action.data.type)
          );
          return createTimeFieldStateFromUpdatedSegment(
            state,
            action.data.type,
            updatedSegment,
            false
          );
        }
      }
      return state;
    }
    case 'updateSegment': {
      const segmentState = findSegment(state.segments, action.data.type);
      // If hour is 1-12, ignore typing '0' under certain circumstances
      if (
        action.data?.text === '0' &&
        action.data.type === 'hour' &&
        segmentState['aria-valuemin'] === 1 &&
        (segmentState.isComplete || segmentState['aria-valuenow'] === undefined)
      ) {
        const updatedSegment = { ...segmentState, digitCount: 1 };
        return createTimeFieldStateFromUpdatedSegment(
          state,
          action.data.type,
          updatedSegment,
          false,
          false
        );
      }
      // Modifies the value of a specific time segment while keeping the rest of the time state intact.
      // This gets called from TimeSegment onKeyDown which is called one key at a time.
      const updatedSegment = updateSegment(action.data.type, segmentState, action);
      return createTimeFieldStateFromUpdatedSegment(state, action.data.type, updatedSegment);
    }
    default: {
      throw new Error('Unknown action type');
    }
  }
};

/**
 * Given the array of TimeSegmentStates and the type, returns the segment with that type.
 * @param segments
 * @param type
 * @returns the segment with that type.
 */
const findSegment = (
  segments: TimeSegmentState[],
  type: EditableTimeSegmentType
): TimeSegmentState => {
  return segments.find((segment) => segment.type === type)!;
};

/**
 * A time segment is considered complete if one of these is true:
 * - digitCount >= targetDigitCount
 * - appending a zero to the current value causes it
 * to exceed the max. For instance, if aria-valuemax which is 12 (for 12-hour times), and
 * 2 is in hour already, if someone typed in 0, it would be 20, and
 * that is greater than the max.
 * 
 * digitCount is used to figure out if the segment is complete. 
 * It is used to distinguish between
 * the user typing in all 0s or the auto-filling of 0s that happens when the user types in one 0 into an empty or complete field.
 * It is also used to figure out if the user typed in the number of digits to fill the space.
 * Like if the user typed in 0, 0, 5 in millisecond. The segment is complete because the digitCount >= targetDigitCount.

 * @param val - the value of the segment for which to check completeness.
 * @param maxVal - the maximum value for the segment. E.g., for seconds it is 59.
 * @param digitCount - the number of digits a user typed.
 * @param targetDigitCount - the number of digits in a segment.
 * @returns true if the segment is considered complete, false otherwise.
 */
const isSegmentComplete = (
  val: number,
  maxVal: number,
  digitCount: number,
  targetDigitCount: number
) => {
  if (digitCount >= targetDigitCount) {
    return true;
  }
  const num = stringToNumber(numberToString(val) + '0');
  return num > maxVal;
};

/**
 * A time is considered complete if all of its segments have values.
 * We use each segment's 'text' property to determine that.
 * @param allSegments
 * @returns {boolean} every segment has a value.
 */
const isTimeComplete = (allSegments: TimeSegmentState[]) => {
  return allSegments.every((seg) => seg.text && seg.text.length > 0);
};

/**
 * A time is considered a partial time if at least one segment has a value.
 * We use each segment's 'aria-valuenow' property to determine that.
 * @param allSegments
 * @returns {boolean} at least one segment has a value.
 */
const isPartialTime = (allSegments: TimeSegmentState[]) => {
  // Even though the dayPeriod field does not read-out the valuenow, we use it
  // to indicate if it is filled in or not.
  return allSegments.some((seg) => seg['aria-valuenow'] !== undefined);
};

/**
 * Construct a time that reflects the values of each segment.
 * If none of the segments have a value, return undefined.
 * If only the dayPeriod has a value, return undefined, since we filter out dayPeriod; Time has 0-23 hour.
 *
 * @param segments
 * @returns The Time that reflects the values of each segment.
 */
const createTimeFromSegments = (segments: TimeSegmentState[]) => {
  const hasAnySegmentWithAValue = isPartialTime(segments);
  if (!hasAnySegmentWithAValue) {
    return undefined;
  }

  // get the dayPeriod's value, if it exists
  const dayPeriodValue = segments.find((seg) => seg.type === 'dayPeriod')?.['aria-valuenow'];

  // get the hour segment's value if it exists.
  const hourSegmentValue = segments.find((seg) => seg.type === 'hour')?.['aria-valuenow'];
  let adjustedHour = hourSegmentValue;

  // if the hour segment has a value and the dayPeriod segment has a value, then
  // we will adjust the hour so that it is a h23 hour.
  if (dayPeriodValue !== undefined && hourSegmentValue !== undefined) {
    adjustedHour = getAdjustedH12ToH23(dayPeriodValue, hourSegmentValue);
  }

  // Filter out dayPeriod segment and map the remaining segments to values
  const segmentValues = segments
    .filter((seg) => seg.type !== 'dayPeriod') // Filter out dayPeriod
    .reduce<Record<string, number | undefined>>((acc, seg) => {
      const value = seg.type === 'hour' ? adjustedHour : seg['aria-valuenow'];
      if (value !== undefined) {
        acc[seg.type] = value;
      }
      return acc;
    }, {});
  // If all that was left in the time segments at this point was the dayPeriod, we would get an empty object
  // since the value does not contain dayPeriod. If this is the case return undefined.
  return Object.entries(segmentValues).length === 0 ? undefined : segmentValues;
};

/**
 * Returns a copy of the currentState.orderedSegments. The orderedSegments is an array of the name of each segment type
 * in the order they appear to the user.
 * E.g., ['hour', 'minute, 'dayPeriod'], when the InputTimeMask component shows the hour, minute, and dayPeriod segments in that order.
 * E.g., ['hour', 'minute', 'second'] when the InputTimeMask component shows the hour, minute, and second segments in that order.
 *
 * @param {TimeFieldState} currentState - the current state of the time field.
 * @returns {OrderedTimeSegmentsArray} the orderedSegments in the current state.
 */
const getOrderedSegmentsFromState = (currentState: TimeFieldState) => {
  // Typescript loses its typing when we spread, so add it back.
  return [...currentState.orderedSegments] as OrderedTimeSegmentsArray;
};

/**
 * Replace the updated segment and return a new TimeSegmentState[].
 * @param existingSegments
 * @param updatedSegmentType
 * @param updatedSegment
 * @returns
 */
const replaceTimeSegment = (
  existingSegments: TimeSegmentState[],
  updatedSegmentType: EditableTimeSegmentType,
  updatedSegment: TimeSegmentState
) => {
  return existingSegments.map((seg) => (seg.type === updatedSegmentType ? updatedSegment : seg));
};

/**
 * Creates a new array of TimeSegmentState objects by updating the specified segment which might
 * affect other segments.
 *
 * If the modified segment type is 'hour', and the 'minute',
 * 'second', 'millisecond', 'dayPeriod' segments, if defined, are not set (i.e., their aria-valuenow is undefined),
 * their values are auto-filled, that is, initialized to 0 or localized version of 'AM' for dayPeriod.
 *
 * If the segment is complete, and autoAdvance is true, then the next segment will be selected.
 * @param {TimeSegmentState[]} existingSegments - The current array of time segments.
 * @param {OrderedTimeSegmentsArray} orderedSegments - The ordered list of segments used to determine
 * the segment sequence for selection and auto-advancing.
 * @param {EditableTimeSegmentType} updatedSegmentType - The type of the segment that is being updated.
 * @param {TimeSegmentState} updatedSegment - The updated segment state to replace the current one.
 * @param {boolean} autoAdvance - whether to automatically select the next segment upon completion of the current segment.
 * @param {boolean} autoFill - whether to automatically fill the next segments upon completion of the current segment.
 * @returns The new array of TimeSegmentState objects, with the segment updated and other segments
 * modified as needed.
 * If autoAdvance is true, the next segment will be automatically set to isSelected upon segment isCompleted.
 */
const createTimeSegmentStates = (
  existingSegments: TimeSegmentState[],
  orderedSegments: OrderedTimeSegmentsArray,
  updatedSegmentType: EditableTimeSegmentType,
  updatedSegment: TimeSegmentState,
  autoAdvance: boolean,
  autoFill: boolean
) => {
  // Replace the segment with the updated version.
  const modifiedSegments = replaceTimeSegment(existingSegments, updatedSegmentType, updatedSegment);
  let updatedSegments = modifiedSegments;

  const isHourSegmentAndComplete = updatedSegmentType === 'hour' && updatedSegment.isComplete;
  if (isHourSegmentAndComplete && autoFill) {
    updatedSegments = autoFillEmptyNonHourSegments(updatedSegments);
  }
  // If the updatedSegment is complete, automatically select the next segment.
  updatedSegments =
    updatedSegment.isComplete && autoAdvance
      ? selectNext(updatedSegments, orderedSegments)
      : updatedSegments;

  return updatedSegments;
};

/**
 * This function auto-fills all empty, non-hour segments ('minute', 'second', 'millisecond' and 'dayPeriod').
 * It only auto-fills the empty fields and leaves the other fields alone.
 * For example, if milliseconds is not empty, we leave the milliseconds as is and update all the empty fields;
 * 23:--:--:400 becomes 23:00:00:400. And if there is a dayPeriod segment, 5:--:--:400 -- becomes 5:00:00:400 AM.
 *
 * @param existingSegments
 * @param hourSegmentState
 * @returns
 */
const autoFillEmptyNonHourSegments = (existingSegments: TimeSegmentState[]) => {
  // if a segment already has a value, then do nothing.
  const initializedSegments = existingSegments.map((seg) => {
    if (seg['aria-valuenow'] !== undefined) {
      return seg;
    }

    let text;
    let ariaValue;

    if (seg.type === 'millisecond') {
      text = '000';
      ariaValue = 0;
    } else if (['minute', 'second'].includes(seg.type)) {
      text = '00';
      ariaValue = 0;
    } else if (seg.type === 'dayPeriod') {
      // seg.amPM.am always has a value for the dayPeriod segment, so there is no need to default it.
      text = seg.amPm?.am;
      ariaValue = AM_VALUE;
    }

    return text !== undefined
      ? {
          ...seg,
          'aria-valuenow': ariaValue,
          text,
          isComplete: true,
          'aria-valuetext': text,
          isAutoFilled: true
        }
      : seg;
  });
  return initializedSegments;
};

/**
 * Constructs a new TimeFieldState given an updated segment, which may affect other segments.
 * Called from actions like updateSegment, stepping/paging/home/end, clearing a segment.
 * @param {TimeFieldState} currentState - The current state of the time field.
 * @param {EditableTimeSegmentType} updatedSegmentType - The type of the segment that was updated.
 * @param {TimeSegmentState} updatedSegment - The updated state of the specified segment.
 * @param {boolean} autoAdvance - Whether to auto-advance to the next segment if the current segment is complete. Defaults to true.
 * @param {boolean} autoFill - Whether to auto-fill to the next segments if the current hour segment is complete. Defaults to true
 * since we almost always want to autoFill. The one instance where we do not auto-fill is when we ignore 0s in the hour field.
 * @returns {TimeFieldState} - A new TimeFieldState based on the updated segment and the current state.
 */
const createTimeFieldStateFromUpdatedSegment = (
  currentState: TimeFieldState,
  updatedSegmentType: EditableTimeSegmentType,
  updatedSegment: TimeSegmentState,
  autoAdvance = true,
  autoFill = true
): TimeFieldState => {
  let updatedSegments = createTimeSegmentStates(
    currentState.segments,
    currentState.orderedSegments,
    updatedSegmentType,
    updatedSegment,
    autoAdvance,
    autoFill
  );
  // If updatedSegmentType is not hour, then set isAutoFilled to false for all segments.
  // The use case that this covers is this:
  // Type in hour, this auto-fills which sets all the segments to isAutoFilled: true, then we render the aria-live.
  // Now go to change the AM field. This sets isAutoFilled: false for that segment, but we do not want to update
  // aria-live to a new time, so at this point we set isAutoFilled to false for all the other segments
  // which will cause aria-live to be cleared. This is why we have this here.
  if (!(updatedSegmentType === 'hour')) {
    updatedSegments = updatedSegments.map((seg) => {
      return { ...seg, isAutoFilled: false };
    });
  }
  return createTimeFieldStateFromUpdatedSegments(
    currentState,
    updatedSegments,
    currentState.isTimeSelected
  );
};

/**
 * The clearSegment function clears the current state of the time segment by resetting
 * specific properties to indicate that the segment no longer holds a value.
 * Specifically, it sets:
 * 'aria-valuetext': undefined,
 * 'aria-valuenow': undefined,
 *  digitCount: 0,
 *  text: undefined,
 *  isComplete: false
 *
 * This function returns a new segment state reflecting these cleared properties.
 *
 * @param {TimeSegmentState} segmentState - The current state of the time segment to clear.
 * @returns {TimeSegmentState} A new segment state with cleared properties.
 */
const clearSegment = (segmentState: TimeSegmentState) => {
  return {
    ...segmentState,
    'aria-valuetext': undefined,
    'aria-valuenow': undefined,
    digitCount: 0,
    text: undefined,
    isComplete: false
  };
};

/**
 * Clears the current state of each segment.
 *
 * @param {TimeSegmentState[]} segments
 * @returns The updated state with all segments cleared.
 */
const clearTime = (segments: TimeSegmentState[]) => {
  return segments.map((segmentState) => {
    const cleared = clearSegment(segmentState);
    return { ...cleared, isAutoFilled: false };
  });
};

/**
 * Updates the state of a time segment based on user input, one character at a time,
 * enforcing range constraints, completeness rules,
 * and zero-padding requirements for the segment types.
 * @param segmentType
 * @param segmentState
 * @param action
 * @returns A new copy of the segment state with updated values.
 */
const updateSegment = (
  segmentType: EditableTimeSegmentType,
  segmentState: TimeSegmentState,
  action: Extract<TimeReducerAction, { actionType: 'updateSegment' }>
) => {
  // Process the input action text
  // actionText - This is one character, the one the user typed.
  const actionText = action.data.text;
  // Get the current segment state.
  const maxValue = segmentState['aria-valuemax']!;
  const minValue = segmentState['aria-valuemin']!;
  // minute, second, millisecond always have leading zeros, but hour might not.
  const hasLeadingZeros = segmentState.hasLeadingZeros ?? minValue === 0;
  const wasComplete = segmentState.isComplete;
  // digitCount is used to figure out if the segment is complete.
  // It is used to distinguish between
  // the user typing in all 0s or the auto-filling of 0s that happens when the user types in one 0 into an empty or complete field.
  // It is also used to figure out if the user typed in the number of digits to fill the space.
  // Like if the user typed in 0, 0, 5 in millisecond. The segment is complete because the digitCount >= targetDigitCount.
  let digitCount = segmentState.digitCount || 0;
  const targetDigitCount = segmentType === 'millisecond' ? 3 : 2;

  // Set requestedValStr based on leading zero rules
  let requestedValStr = segmentState.text || '';
  if (wasComplete) {
    // If the value is complete and you type another number, it replaces what is already there.
    requestedValStr = actionText;
  } else {
    requestedValStr = (segmentState.text ?? '') + actionText;
  }

  if (hasLeadingZeros && requestedValStr.length > targetDigitCount) {
    requestedValStr = requestedValStr.slice(requestedValStr.length - targetDigitCount);
  } else if (hasLeadingZeros) {
    requestedValStr = requestedValStr.padStart(targetDigitCount, '0');
  }

  digitCount += 1;

  // Check if the value is in range and apply final adjustments
  // if not in range, then actionText overwrites requestedValStr.
  // E.g., first we type '2', then we type '5'. '25' is out of range for hour24, so we use '5'.
  const inRange = withinRange(stringToNumber(requestedValStr), minValue, maxValue);
  let finalValStr = inRange
    ? requestedValStr
    : hasLeadingZeros
    ? actionText.padStart(targetDigitCount, '0')
    : actionText;
  const finalVal = stringToNumber(finalValStr);

  // Determine if the segment is complete.
  const isComplete = isSegmentComplete(finalVal, maxValue, digitCount, targetDigitCount);
  // reset digitCount if the segment is complete.
  // When digitCount is 0, whatever is in the field will be overwritten if the user types into it again,
  // which is the desired behavior.
  digitCount = isComplete ? 0 : digitCount;

  // do the opposite of padStart by removing the leading zero if it is complete.
  if (
    hasLeadingZeros === false &&
    segmentType === 'hour' &&
    isComplete &&
    finalValStr.startsWith('0')
  ) {
    finalValStr = finalValStr.substring(1);
  }
  return {
    ...segmentState,
    'aria-valuetext': finalValStr,
    'aria-valuenow': finalVal,
    digitCount,
    text: finalValStr,
    isComplete,
    isAutoFilled: false
  };
};

/**
 * Updates the selection state of the segments, setting the isSelected property to true for the
 * segment that has the segmentType and false for all others.
 * @param {TimeSegmentState[]} segments
 * @param {EditableTimeSegmentType} segmentType - The type of the segment that is being updated.
 * @returns {TimeSegmentState[]} A new array of time segments with updated isSelected state.
 */
const selectSegment = (segments: TimeSegmentState[], segmentType: EditableTimeSegmentType) => {
  return segments.map((seg) => {
    return { ...seg, isSelected: seg.type === segmentType ? true : false };
  });
};

/**
 * Updates the isSelected state of the first segment from the ordered segments to true,
 * while setting isSelected to false for all other segments.
 *
 * This function can be used to set the initial focus on the first time segment.
 *
 * @param {TimeSegmentState[]} segments - An array of current time segment states, each
 * representing different parts of the time (e.g., hour, minute, second, dayPeriod).
 * @param {OrderedTimeSegmentsArray} orderedSegments - An array of segment types in the
 * represented order (e.g., ['hour','minute', 'dayPeriod']). This is used to get the type
 * of the first segment.
 * @returns {TimeSegmentState[]} A new array of time segment states with the first segment's isSelected
 * property set to true and all other segment's isSelected properties set to false.
 */
const selectFirst = (segments: TimeSegmentState[], orderedSegments: OrderedTimeSegmentsArray) => {
  return selectSegment(segments, orderedSegments[0]);
};

// Find the index of the selected segment in the ordered list; return -1 if there is no selection.
const getSelectedSegmentIndex = (
  segments: TimeSegmentState[],
  orderedSegments: OrderedTimeSegmentsArray
) => {
  const sel = segments.find((seg) => seg.isSelected === true);
  return sel === undefined ? -1 : orderedSegments.findIndex((type) => type === sel.type);
};

/**
 * Convenience function to mark the selected segment complete if it has a value.
 * @param segments
 * @returns The updated segments where if isSelected was true and it has a value, isComplete is now also true.
 */
const markSelectedSegmentComplete = (segments: TimeSegmentState[]) => {
  return segments.map((seg) =>
    seg.isSelected && seg['aria-valuenow'] !== undefined
      ? { ...seg, isComplete: true, digitCount: 0 }
      : seg
  );
};

/**
 * Selects the segment following the one that is currently selected.
 * @param segments
 * @param orderedSegments
 * @returns
 */
const selectNext = (segments: TimeSegmentState[], orderedSegments: OrderedTimeSegmentsArray) => {
  const selectedIndex = getSelectedSegmentIndex(segments, orderedSegments);
  const max = orderedSegments.length - 1;

  // If we're already at the last position, do nothing. Note if nothing was currently selected
  // then selectedIndex is -1. The first segment will be selected.
  return selectedIndex === max
    ? segments
    : selectSegment(markSelectedSegmentComplete(segments), orderedSegments[selectedIndex + 1]);
};

/**
 * Selects the segment prior to the one that is currently selected.
 * @param segments
 * @param orderedSegments
 * @returns
 */
const selectPrevious = (
  segments: TimeSegmentState[],
  orderedSegments: OrderedTimeSegmentsArray
) => {
  const selectedIndex = getSelectedSegmentIndex(segments, orderedSegments);
  return selectedIndex === 0
    ? segments
    : selectedIndex === -1
    ? selectFirst(segments, orderedSegments)
    : selectSegment(markSelectedSegmentComplete(segments), orderedSegments[selectedIndex - 1]);
};

/**
 * Returns a new TimeSegmentState[] where all segments are unselected and any values are
 * marked complete. We want to do this when the user commits the TimeField value (on blur).
 * We do not do this when the user commits the TimeField value on Enter
 * because in that case the component still has focus on Enter, and they could continue typing in the segment.
 * @param segments
 * @returns a new TimeSegmentState[] where all segments have isSelected: false and any
 * with text has isComplete: true.
 */
const deselectAndComplete = (segments: TimeSegmentState[]) => {
  return segments.map((seg) => {
    return { ...seg, isSelected: false, isComplete: !!seg.text };
  });
};

/**
 * Returns a new TimeSegmentState[] where all segments are unselected by setting isSelected: false.
 * @param segments
 * @returns
 */
const clearSelection = (segments: TimeSegmentState[]) => {
  return segments.map((seg) => {
    return { ...seg, isSelected: false };
  });
};

/**
 * Checks if hour is clear and dayPeriod is not clear, and if so, clear the dayPeriod segment.
 * @param segments
 * @returns segments, with the 'dayPeriod' segment updated, if needed.
 */
const clearDayPeriodSegmentWhenHourIsEmpty = (segments: TimeSegmentState[]) => {
  // find the dayPeriod segment if it exists.
  const dayPeriodSegment = segments.find((seg) => seg.type === 'dayPeriod');
  // find the hour segment.
  const hourSegmentState = segments.find((seg) => seg.type === 'hour');
  const hourSegmentHasText = hourSegmentState?.text !== undefined;
  const dayPeriodSegmentHasText = dayPeriodSegment?.text !== undefined;

  // If hour is clear and dayPeriod is not clear, clear dayPeriod.
  // E.g., --:00 PM becomes --:00 --.
  if (!hourSegmentHasText && dayPeriodSegmentHasText) {
    return segments.map((seg) => {
      if (seg.type === 'dayPeriod') {
        return clearSegment(seg);
      }
      return { ...seg };
    });
  }
  return segments;
};

/**
 * If the hour segment is filled in, auto-fill empty minute, second, and millisecond segments and dayPeriod segment.
 * The additional parameter checkHourSelected, if true, will make sure that the hour isSelected state is true before auto-filling.
 * This is used for the 'selectNext' action.
 * @param segments
 * @param checkHourSelected {boolean} true, if the hour segment must be selected in order to auto-fill. This is the case for the 'selectNext' action.
 * @returns segments, with the minute, second, millisecond, and dayPeriod segments updated, if needed.
 */
const completeAllEmptySegmentsWhenHourHasValue = (
  segments: TimeSegmentState[],
  checkHourSelected?: boolean
) => {
  const hourSegment = segments.find((seg) => seg.type === 'hour');
  const doAutoFill = checkHourSelected
    ? hourSegment?.isSelected && hourSegment?.text !== undefined
    : hourSegment?.text !== undefined;
  const updatedSegments = doAutoFill ? autoFillEmptyNonHourSegments(segments) : segments;
  return updatedSegments;
};

/**
 * If the hour segment is filled in, auto-fill empty minute, second, and millisecond segments and dayPeriod segment.
 * If the hour segment is empty and the dayPeriod segment is filled in, clear out the dayPeriod segment.
 * This is what we do when we leave the component.
 * @param segments
 * @returns segments, with the minute, second, millisecond, and dayPeriod segments updated, if needed.
 */
const completeAllSegmentsBasedOnHour = (segments: TimeSegmentState[]) => {
  const updatedSegments = completeAllEmptySegmentsWhenHourHasValue(segments);
  return clearDayPeriodSegmentWhenHourIsEmpty(updatedSegments);
};

/**
 * This function is called when the user wants to select the entire date.
 * If currentState.isCompleteTime is true,
 * - returns a new TimeFieldState from the currentState where the entire time is marked as selected,
 * and the 'isSelected' property of all the individual segments is set to false.
 *
 * If the time is not complete,
 * - returns the currentState unchanged.

 * @param {TimeFieldState} currentState
 * @returns
 */
const selectTime = (currentState: TimeFieldState) => {
  if (currentState.isCompleteTime) {
    return createTimeFieldStateFromUpdatedSegments(
      currentState,
      clearSelection(currentState.segments),
      true
    );
  }
  return currentState;
};

/**
 * Creates a new TimeFieldState from the updatedSegments and the isTimeSelected param.
 * NOTE: This updates currentTime based on the segments.
 *
 * @param {TimeFieldState} currentState
 * @param {TimeFieldState[]} updatedSegments
 * @param {boolean} isTimeSelected - Indicates whether the entire time field is selected.
 * @returns {TimeFieldState} The updated state of the time field.
 */
const createTimeFieldStateFromUpdatedSegments = (
  currentState: TimeFieldState,
  updatedSegments: TimeSegmentState[],
  isTimeSelected: boolean
) => {
  const orderedSegmentsFromCurrentState = getOrderedSegmentsFromState(currentState);
  const timeBeforeUpdate = currentState.currentTime;
  const timeAfterUpdate = createTimeFromSegments(updatedSegments);
  let useUpdatedValue = true;
  // if either one of these is undefined, then go ahead and update the value
  if (timeBeforeUpdate !== undefined && timeAfterUpdate !== undefined) {
    // Handle the case where the user has not updated the value, but the value we create from the segments is different only because
    // the new time represents the time with the segments shown. We do not want to update the time in this case.
    // Example, initial time value is {hour: 1, minute: 29, second: 66, millisecond: 888}, and granularity='minute', so orderedSegments is ['hour', 'minute'].
    // The updated time will be {hour:1, minute: 29}. In this case the user hasn't change the time. It's only different because of the segments, so we leave the initial time.
    const hasExtraSegments = hasSegmentsNotDisplayed(
      orderedSegmentsFromCurrentState,
      timeBeforeUpdate
    );
    if (hasExtraSegments) {
      useUpdatedValue = displayedTimeSegmentsAreDifferent(
        orderedSegmentsFromCurrentState,
        timeBeforeUpdate,
        timeAfterUpdate
      );
    }
  }

  // If any of the segments have isAutoFilled set to true, then set the state isTimeAutoFilled.
  const isAnySegmentAutoFilled = updatedSegments.some((seg) => seg.isAutoFilled === true);

  return {
    isCommitting: currentState.isCommitting,
    currentTime: useUpdatedValue ? timeAfterUpdate : timeBeforeUpdate,
    isTimeAutoFilled: isAnySegmentAutoFilled,
    isCompleteTime: isTimeComplete(updatedSegments),
    isTimeSelected,
    isPartialTime: isPartialTime(updatedSegments),
    orderedSegments: orderedSegmentsFromCurrentState,
    segments: updatedSegments
  };
};

/**
 * Gets the minimum value for the segment.
 * @param segmentState
 * @returns the minimum value for the segment.
 */
const getHomeSegmentValue = (segmentState: TimeSegmentState) => {
  return segmentState['aria-valuemin']!;
};

/**
 * Gets the maximum value for the segment.
 * @param segmentState
 * @returns the maximum value for the segment
 */
const getEndSegmentValue = (segmentState: TimeSegmentState) => {
  return segmentState['aria-valuemax']!;
};

/**
 * Adjusts the value of a time segment (e.g., hour, minute, second, millisecond) by a given step.
 * It handles both increasing and decreasing the value with wrapping logic
 * when the value exceeds the max or falls below the min.
 *
 * Uses separate paths for cases when min is 0 and when min is non-zero for clarity.
 *
 * @param {number} value - The current value of the segment.
 * @param {number} step - The amount to increment or decrement the value.
 * @param {number} min - The minimum allowed value for the segment.
 * @param {number} max - The maximum allowed value for the segment.
 * @param {DirectionType} direction - Whether to increase or decrease the value.
 * @returns {number} - The new adjusted value after wrapping.
 */
const adjustTimeSegment = (
  value: number,
  step: number,
  min: number,
  max: number,
  direction: DirectionType
) => {
  const range = max - min + 1; // Total values in the range.

  if (min === 0) {
    // Path for min === 0 (e.g., minutes, seconds, 0-23 hour time).
    if (direction === 'increase') {
      const steppedValue = value + step;
      return steppedValue % range; // Wrap within range 0 to max.
    } else {
      // direction === 'decrease'
      const steppedValue = value - step;
      return (steppedValue + range) % range; // Wrap below min to max.
    }
  } else {
    // Path for any min !== 0 (e.g., 12-hour clock has min 1 and max 12, or other ranges).
    if (direction === 'increase') {
      const steppedValue = value + step;
      return ((steppedValue - min) % range) + min; // Wrap within min-based range.
    } else {
      // direction === 'decrease'
      const steppedValue = value - step;
      return ((steppedValue - min + range) % range) + min; // Wrap below min to max.
    }
  }
};

/**
 * Returns the result of stepping currentValue in the requested direction, taking into account min and max.
 * @param {number} currentValue
 * @param direction
 * @param {number} min
 * @param {number} max
 * @returns {number} the result of stepping currentValue in the requested direction, taking into account min and max.
 */
const getSteppedSegmentValue = (
  currentValue: number,
  direction: DirectionType,
  min: number,
  max: number
) => {
  const newValue = adjustTimeSegment(currentValue, 1, min, max, direction);
  return newValue;
};

/**
 * Returns the result of paging the value up or down, taking into account min and max.
 * @param currentValue
 * @param direction
 * @param min
 * @param max
 * @param segmentType
 * @returns
 */
const getPagedSegmentValue = (
  currentValue: number,
  direction: DirectionType,
  min: number,
  max: number,
  segmentType: Exclude<EditableTimeSegmentType, 'dayPeriod'>
) => {
  let step;
  if (segmentType === 'minute' || segmentType == 'second') {
    step = 10;
  } else if (segmentType === 'millisecond') {
    step = 100;
  } else {
    step = 2;
  }

  const newValue = adjustTimeSegment(currentValue, step, min, max, direction);
  return newValue;
};

/**
 * Creates a new segment state with an updated value from a keyboard action such as step, page, Home, or End.
 * After any keyboard action, the value is complete so that if the user types anything,
 * it will replace the stepped value.
 * @param {EditableTimeSegmentType} segmentType - The type of the segment that is being updated.
 * @param {number} newVal - new value which is used to update the aria-valuenow and aria-valuetext and text properties.
 * @param segmentState
 * @returns A new segment state with an updated value from a keyboard action such as step, page, Home, or End.
 */
const createSegmentFromKeyboardAction = (
  segmentType: EditableTimeSegmentType,
  newVal: number,
  segmentState: TimeSegmentState
) => {
  // Only the hour12 segment has aria-valuemin to be 1. All the other segments' aria-valuemin is 0.
  // Most locales that have hour12 by default also do not pad zero so this is why it is the default.
  const padZero = segmentState.hasLeadingZeros ?? segmentState['aria-valuemin'] === 0;
  const ariaValueText = getAriaValueText(newVal, segmentState, segmentType, padZero);

  // Create a new segment state that reflects the updated value.
  // After any keyboard action, the value is complete so that if the user types anything,
  // it will replace the stepped value.
  return {
    ...segmentState,
    'aria-valuetext': ariaValueText,
    'aria-valuenow': newVal,
    text: ariaValueText,
    isComplete: true,
    isAutoFilled: false
  };
};

// Set the segment to its minimum value.
const goToHome = (segmentType: EditableTimeSegmentType, segmentState: TimeSegmentState) => {
  const newValue = getHomeSegmentValue(segmentState); // gets aria-valuemin
  return createSegmentFromKeyboardAction(segmentType, newValue, segmentState);
};

// Set the segment to its maximum value.
const goToEnd = (segmentType: EditableTimeSegmentType, segmentState: TimeSegmentState) => {
  const newValue = getEndSegmentValue(segmentState); // gets aria-valuemax
  return createSegmentFromKeyboardAction(segmentType, newValue, segmentState);
};

/**
 * Steps or pages a segment's value. Pass in initialStarting of 12 for hour12, otherwise we want to use the min.
 * Creates a new segment state with an updated value.
 *
 * After any keyboard action, the value is complete so that if the user types anything, it will replace the stepped value.
 * @param segmentType
 * @param segmentState
 * @param type
 * @param direction
 * @returns A new segment state with an updated value from the keyboard action.
 */
const doStepOrPage = (
  segmentType: EditableTimeSegmentType,
  segmentState: TimeSegmentState,
  type: 'step' | 'page',
  direction: DirectionType
) => {
  // aria-valuemin is 0 for all segments except the 12-hour hour segment, where it is 1.
  // The Redwood design states that when you step or page up or down when there is no initial value in the segment,
  // it initializes the hour segment to 12, and auto-fills any empty segments for a 12-hour clock,
  // and it initializes the hour segment to 0, and auto-fills any empty segments for a 24-hour clock.
  // To get the initial value, we use the aria-valuemin for all segments (which is 0) except for the 12-hour hour segment.
  // For the 12-hour segment stepping or paging starts at 12.
  const initialStarting =
    segmentType === 'hour' && segmentState['aria-valuemin'] === 1
      ? 12
      : segmentState['aria-valuemin'];
  const valueNow = segmentState['aria-valuenow'];
  const min = segmentState['aria-valuemin']!;
  const max = segmentState['aria-valuemax']!;

  let newValue;
  if (segmentType === 'dayPeriod') {
    // toggle aria-valuenow
    newValue = valueNow === AM_VALUE ? PM_VALUE : AM_VALUE;
  } else if (valueNow === undefined) {
    // start at the minimum if there is no value.
    newValue = initialStarting ?? min;
  } else if (type === 'page') {
    newValue = getPagedSegmentValue(valueNow, direction, min, max, segmentType);
  } else {
    newValue = getSteppedSegmentValue(valueNow, direction, min, max);
  }
  return createSegmentFromKeyboardAction(segmentType, newValue, segmentState);
};

/**
 * Get the text for the segment's aria-valuetext property. For dayPeriod segment, we use the segmentState.amPm?.am and
 * segmentState.amPm?.pm to get the localized value.
 * @param {number} newVal - the segments value.
 * @param {TimeSegmentState} segmentState - the segment's current state.
 * @param {EditableTimeSegmentType} segmentType - the segment's type.
 * @param {boolean} padZero - whether to pad the text with 0, e.g., minute segments pad 0, so 1 minute would be '01'.
 * @returns the text for the segment's aria-valuetext property.
 */
const getAriaValueText = (
  newVal: number,
  segmentState: TimeSegmentState,
  segmentType: EditableTimeSegmentType,
  padZero: boolean
) => {
  if (segmentType === 'dayPeriod') {
    // This is the segmentState for dayPeriod:
    //
    // aria-valuenow="0” // 0 if AM, 12 if PM
    // aria-valuetext="AM”
    // aria-valuemin="0”
    // aria-valuemax=“12"
    // text=“AM"
    // aria-label="AM/PM"
    // seg.amPM.am and pm always have a value for the dayPeriod segment, so there is no need to default them.
    const amValue = segmentState.amPm?.am;
    const pmValue = segmentState.amPm?.pm;
    return newVal < PM_VALUE ? amValue : pmValue;
  }
  const valStr = numberToString(newVal);
  if (!padZero) {
    return numberToString(newVal);
  }
  const targetDigitCount = segmentType === 'millisecond' ? 3 : 2;
  return valStr.padStart(targetDigitCount, '0');
};

/**
 * Returns an hour 0-23 given the hour 1-12 and the dayPeriod 0 for AM and 12 for PM.
 * @param dayPeriodValue
 * @param timeHour12
 * @returns The hour 0-23.
 */
const getAdjustedH12ToH23 = (dayPeriodValue: number, timeHour12: number) => {
  // If both dayPeriod and hour are available, adjust the hour to h23 format
  if (dayPeriodValue === AM_VALUE && timeHour12 === 12) {
    return 0; // 12 AM (midnight) adjustment.
  }
  if (dayPeriodValue === PM_VALUE && timeHour12 === 12) {
    return 12; // 12 PM, no adjustment needed
  }
  if (dayPeriodValue === PM_VALUE) {
    return timeHour12 + 12; // Convert PM hour to h23 format
  }
  return timeHour12; // AM hours, no adjustment needed.
};

export { timeReducer, numberToString, stringToNumber };
export type { TimeReducerAction, DirectionType };
